<template>
  <Teleport to="body">
    <Transition name="sf-drawer">
      <div
        v-if="open"
        class="tw-fixed tw-inset-0 tw-z-drawer"
        data-testid="sf-drawer"
      >
        <div
          class="tw-absolute tw-inset-0 tw-bg-modal-overlay-docked"
          data-testid="sf-drawer-backdrop"
        />

        <aside
          ref="panel"
          class="
            sf-drawer__panel
            tw-absolute
            tw-inset-y-0
            tw-right-0
            tw-flex
            tw-max-w-full
            tw-flex-col
            tw-bg-white
            tw-shadow-lg
          "
          :style="{ width }"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
          data-testid="sf-drawer-panel"
          @keydown.tab="trapTab"
        >
          <header
            class="
              tw-flex
              tw-shrink-0
              tw-items-center
              tw-gap-2.5
              tw-border-x-0
              tw-border-t-0
              tw-border-b
              tw-border-solid
              tw-border-sf-slate-lighter-1
              tw-px-6
              tw-py-2.5
            "
          >
            <h2
              :id="titleId"
              class="tw-m-0 tw-text-lg tw-font-medium tw-text-sf-slate-dark-2"
              data-testid="sf-drawer-title"
            >
              {{ title }}
            </h2>

            <slot name="header" />

            <SfButtonDs
              variant="tertiary"
              icon-only
              icon-before="xmark"
              class="tw-ml-auto tw-shrink-0"
              :aria-label="I18n.t('common.close')"
              data-testid="sf-drawer-close"
              @click="emit('close')"
            />
          </header>

          <div
            class="tw-flex-1 tw-overflow-y-auto tw-p-6"
            data-testid="sf-drawer-body"
          >
            <slot />
          </div>

          <footer
            v-if="$slots.footer"
            class="
              tw-flex
              tw-shrink-0
              tw-items-center
              tw-justify-end
              tw-gap-2
              tw-border-x-0
              tw-border-b-0
              tw-border-t
              tw-border-solid
              tw-border-sf-slate-lighter-1
              tw-px-6
              tw-py-3
            "
            data-testid="sf-drawer-footer"
          >
            <slot name="footer" />
          </footer>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core'
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'

import { FOCUSABLE } from '@app/shared/vue/components/sf-drawer/constants'
import { SfButtonDs } from '@silverfin/ui-components'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({ name: 'SfDrawer' })

interface Props {
  open: boolean
  title: string
  width?: string
  closeOnEscape?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  width: '680px',
  closeOnEscape: true,
})

const emit = defineEmits<{ close: [] }>()

const I18n = useI18n()

const titleId = `sf-drawer-title-${useId()}`

const panel = ref<HTMLElement | null>(null)

// The element the drawer was opened from. Closing hands focus back to it, so a
// keyboard user carries on from the row or the button they left rather than
// from the top of the page.
let trigger: HTMLElement | null = null

// A browser drops what CSS hides, including an element under a hidden
// ancestor, which a computed display check misses. jsdom has neither layout
// nor checkVisibility, so there the specs keep every element they render.
function isTabbable(element: HTMLElement): boolean {
  if (element.hidden) return false
  if (typeof element.checkVisibility !== 'function') return true

  return element.checkVisibility({ visibilityProperty: true })
}

function focusableItems(): HTMLElement[] {
  if (!panel.value) return []

  return Array.from(
    panel.value.querySelectorAll<HTMLElement>(FOCUSABLE)
  ).filter(isTabbable)
}

function trapTab(event: KeyboardEvent): void {
  const items = focusableItems()
  const first = items[0]
  const last = items[items.length - 1]
  const active = document.activeElement

  if (event.shiftKey && (active === first || active === panel.value)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (props.open && event.key === 'Escape' && props.closeOnEscape) emit('close')
})

async function activate(): Promise<void> {
  trigger = document.activeElement as HTMLElement | null

  await nextTick()

  const [first] = focusableItems()
  ;(first ?? panel.value)?.focus()
}

function deactivate(): void {
  trigger?.focus()
  trigger = null
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      activate()
    } else if (trigger) {
      deactivate()
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  if (props.open) deactivate()
})
</script>

<style lang="scss" scoped>
.sf-drawer {
  &-enter-active,
  &-leave-active {
    transition: opacity 0.2s ease;

    .sf-drawer__panel {
      transition: transform 0.2s ease;
    }
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;

    .sf-drawer__panel {
      transform: translateX(100%);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .sf-drawer-enter-active,
  .sf-drawer-leave-active {
    transition: none;

    .sf-drawer__panel {
      transition: none;
    }
  }
}
</style>
